import { Pie, PieChart } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Card } from "@/components/ui/card";
import { useDilemmasPerTopic } from "../hooks/useFetchDilemmasPerTopic";
import { useMemo } from "react";
import { cn, toSnakeCase } from "@/lib/utils";
import { useAtomValue } from "jotai";
import { statsDateRangeAtom } from "@/stores/atoms/stats-date-range-atom";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";

function topicColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 88%, 50%)`;
}

const DilemmasPerTopicStats = () => {
  const { t } = useTranslation();
  const range = useAtomValue(statsDateRangeAtom);
  const { data: dilemmasPerTopic, isLoading: isLoadingDilemmas } =
    useDilemmasPerTopic({
      start_date: range.start_date,
      end_date: range.end_date,
    });

  const chartData = dilemmasPerTopic?.map((d) => ({
    topic: toSnakeCase(d.topic_name),
    value: d.total,
    fill: `var(--color-${toSnakeCase(d.topic_name)})`,
  }));

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {
      total: {
        label: t("modules.stats.total"),
      },
    };
    dilemmasPerTopic?.forEach((d) => {
      config[toSnakeCase(d.topic_name)] = {
        label: d.topic_name,
        color: topicColor(d.topic_name),
      };
    });
    return config;
  }, [dilemmasPerTopic, t]);

  if (isLoadingDilemmas) {
    return <Skeleton className="w-full lg:w-108.5 h-75" />;
  }

  return (
    <Card className="p-4 pb-6 w-full lg:w-auto lg:pb-4">
      <p className="font-semibold">
        {t("modules.stats.dilemmas_per_topic_stats")}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
        <ChartContainer config={chartConfig} className="aspect-square w-52">
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="topic"
              outerRadius="80%"
              innerRadius="60%"
            />
          </PieChart>
        </ChartContainer>
        <ul className="flex-1 max-w-64 flex flex-col gap-1">
          {dilemmasPerTopic?.map((d) => (
            <li
              key={d.topic_name}
              className="text-sm text-gray-600 flex items-center gap-2"
            >
              <div
                className={cn("size-3 rounded-full")}
                style={{ backgroundColor: topicColor(d.topic_name) }}
              ></div>
              {d.topic_name}
              <p>
                (<span className="font-semibold">{d.total}</span>)
              </p>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

export default DilemmasPerTopicStats;
